/**
 * TypeScript types matching the backend Pydantic shapes EXACTLY (BUG-017 contract drift prevention).
 *
 * Plan 01-08's Step 5.5 contract-test wave will assert this match at runtime against the
 * running stack — every shape declared here must round-trip with the actual backend response.
 *
 * Sources:
 *   - TokenResponse / UserPublic — plan 01-03 (backend/app/schemas/auth.py)
 *   - UserOut / UserListPage / UserCreate / UserUpdate — plan 01-05 (backend/app/schemas/user.py)
 *   - AuditLogEntry / AuditLogPage — plan 01-04 (backend/app/schemas/audit.py)
 */
export type Role =
  | "content_admin"
  | "super_admin"
  | "hr_manager"
  | "hr_recruiter"
  | "hiring_manager";

export type Lang = "es" | "en" | "de";

export interface UserPublic {
  id: number;
  username: string;
  role: Role;
  preferred_lang: Lang;
  password_must_rotate: boolean;
}

export interface UserOut extends UserPublic {
  is_active: boolean;
  locked_until: string | null;
  failed_login_count: number;
}

export interface TokenResponse {
  access_token: string;
  token_type: "bearer";
  expires_in: number;
  user: UserPublic;
}

export interface UserListPage {
  items: UserOut[];
  total: number;
  limit: number;
  offset: number;
}

export interface AuditLogEntry {
  id: number;
  timestamp: string; // ISO8601
  user_id: number | null;
  user_role: Role | null;
  action: string;
  entity_type: string;
  entity_id: number;
  diff: Record<string, unknown> | null;
  correlation_id: string | null;
  is_archived?: boolean; // AUD-04: true for rows served from the archive table
}

export interface AuditLogPage {
  items: AuditLogEntry[];
  total: number;
  limit: number;
  offset: number;
}

/**
 * FastAPI 4xx envelopes come in three shapes:
 *   1. ``{detail: "string"}`` — most 4xx (401 invalid_credentials, 409 username_taken)
 *   2. ``{detail: [{loc, msg, type}, ...]}`` — 422 pydantic validation errors
 *   3. ``{detail: {error: "...", missing: [...]}}`` — domain-specific composite errors
 *      (e.g. compliance switch-to-live with missing fields)
 */
export interface ApiErrorBody {
  detail:
    | string
    | Array<{ loc: (string | number)[]; msg: string; type: string }>
    | { error?: string; missing?: string[] };
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public body: ApiErrorBody,
    public traceId?: string,
  ) {
    super(typeof body.detail === "string" ? body.detail : "api_error");
    // Restore the prototype chain for ``instanceof`` checks under ES5/ES2015 down-targets.
    Object.setPrototypeOf(this, ApiError.prototype);
  }
}
